/**
 * Single source of truth for user-facing PRICE DISPLAY strings.
 *
 * The PROBLEM this fixes: every landing page (resume / career-guides /
 * cover-letters / interview-questions, both hub and `[slug]`) hardcoded the
 * string literal `$9` and `$9/month` in its upsell CTAs. Those literals drifted
 * from what checkout actually charges:
 *   - they showed `$9/bulan` to Indonesian (`id`) users, but the Indonesia rail
 *     (Mayar) charges **Rp 39.000/mo**, not USD — a wrong price shown to the home
 *     market;
 *   - they never reflected the real catalog amounts (`GLOBAL_USD_MINOR`,
 *     `PRODUCTS`), so a price change in the catalog left every landing CTA stale.
 *
 * The FIX: derive every display string here from the SAME catalog constants the
 * checkout charges from —
 *   - global (USD) prices: `GLOBAL_USD_MINOR` (src/lib/billing/catalog-global.ts)
 *   - Indonesia (IDR) prices: `PRODUCTS` (src/lib/mayar/products.ts)
 * — so display can never drift from the charge. `scripts/check-price-literals.ts`
 * (in `pnpm ci:guards`) fails the PR if a raw price literal (`$9`, `/mo`, …)
 * reappears in a UI file outside this module.
 *
 * This is DISPLAY only. It does NOT decide what a user is charged — that stays
 * in checkout.ts / resolveSeekerPrice(), which already read the same constants.
 * It also does NOT shadow any PostHog flag: the IDR price-ladder experiment
 * (`pricing-v2`) only repriced LOGGED-IN cohorts on the /pricing page; these
 * landing CTAs are top-of-funnel teasers that have always shown the catalog
 * (control) price, so deriving from the catalog here keeps that behavior exactly
 * and never reads or shadows the flag.
 *
 * Locale → market mapping for these top-of-funnel teasers is intentionally
 * geo-free: `locale === "id"` deterministically means the Indonesia (IDR) rail
 * (the same SEO-safety rule `getMarket()` enforces), so a hub page that is ISR
 * (no per-request geo) still shows a correct, locale-appropriate price without
 * being forced dynamic. Non-`id` locales show the global USD lead price.
 */

import { GLOBAL_USD_MINOR } from "@/lib/billing/catalog-global";
import { PRODUCTS, type ProductKey } from "@/lib/mayar/products";

type DisplayLocale = "id" | "en";

/** USD from minor units (cents); no trailing cents when whole (900 → "$9"). */
function formatUsd(minor: number): string {
	const dollars = minor / 100;
	return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/** IDR major units → "Rp 39.000" (id-ID grouping). */
function formatIdr(amount: number): string {
	return `Rp ${amount.toLocaleString("id-ID")}`;
}

/**
 * The lead monthly price string for a given locale, sourced from the live
 * catalog. `id` → Indonesia IDR rail (PRODUCTS.pro_monthly); anything else →
 * global USD rail (GLOBAL_USD_MINOR.pro_monthly).
 */
export function monthlyPriceDisplay(locale: string): string {
	return locale === "id"
		? formatIdr(PRODUCTS.pro_monthly.amountIdr)
		: formatUsd(GLOBAL_USD_MINOR.pro_monthly);
}

/**
 * The canonical IDR monthly Pro price as a display string ("Rp 39.000"),
 * sourced from `PRODUCTS.pro_monthly.amountIdr`. For lifecycle emails and
 * policy copy that always quote the Indonesia-rail price regardless of the
 * recipient's locale branch — deriving here (instead of a hardcoded
 * `Rp 39.000` literal) keeps the copy in lockstep with the catalog when the
 * pro_monthly amount changes, and stays correct for the grandfathered cohort
 * under the `pricing-v2` ramp (the catalog amount IS the grandfathered price).
 */
export function proMonthlyIdrDisplay(): string {
	return formatIdr(PRODUCTS.pro_monthly.amountIdr);
}

/**
 * Canonical IDR quarterly (3-month) Pro price ("Rp 129.000"), sourced from
 * `PRODUCTS.pro_quarterly.amountIdr`. Same rationale as
 * {@link proMonthlyIdrDisplay} — used by the touch-3 abandoned-checkout
 * discount email so the quarterly pitch never drifts from the catalog.
 */
export function proQuarterlyIdrDisplay(): string {
	return formatIdr(PRODUCTS.pro_quarterly.amountIdr);
}

/**
 * Canonical IDR annual Pro price ("Rp 299.000"), sourced from
 * `PRODUCTS.pro_annual.amountIdr`. Same rationale as
 * {@link proMonthlyIdrDisplay}: keeps policy/lifecycle copy in lockstep with
 * the catalog when the annual amount changes.
 */
export function proAnnualIdrDisplay(): string {
	return formatIdr(PRODUCTS.pro_annual.amountIdr);
}

/**
 * Canonical IDR founding-annual Pro price ("Rp 199.000"), sourced from
 * `PRODUCTS.pro_annual_founding.amountIdr`. The founding tier is a
 * limited-cohort price-locked SKU; deriving its display here (not a literal)
 * keeps the policy page honest if the founding amount ever changes.
 */
export function proAnnualFoundingIdrDisplay(): string {
	return formatIdr(PRODUCTS.pro_annual_founding.amountIdr);
}

/**
 * Format an arbitrary IDR amount (raw rupiah, e.g. `39_000` → "Rp 39.000") for
 * display. Exposed so the abandoned-checkout email can show the EXACT amount a
 * recipient was about to pay — read from their `checkout_intents` row — which
 * may differ from the catalog default under a pricing experiment, an annual /
 * founding intent, or a token test price. Falls back to
 * {@link proMonthlyIdrDisplay} at the call site when no intent amount exists.
 */
export function formatIdrAmount(amount: number): string {
	return formatIdr(amount);
}

/**
 * The full "amount/interval" price line for an abandoned-checkout intent, in
 * the recipient's locale AND currency. Currency follows the intent's `market`:
 *   - `global` (Gumroad/BMac/USD) — USD price from the catalog
 *     (`GLOBAL_USD_MINOR`); `amountIdr` is 0 for global intents (the USD rail
 *     has no IDR amount), so the price comes from `productKey`, never
 *     `amountIdr`. Without this branch a USD abandoner's email quoted "Rp 0".
 *   - `indonesia` (Mayar/IDR) — the intent's actual `amountIdr` (not the
 *     catalog default), falling back to the catalog monthly when 0/missing
 *     (legacy payloads predating amount threading).
 * Derives the interval from `productKey` so an annual / founding intent shows
 * "/year" ("/tahun"), not "/month". Pure + client-safe (no db import) —
 * `productKey` is a free-text label so the annual check is a `startsWith`
 * prefix match, mirroring `isAnnualKey` without pulling the db-coupled billing
 * module into client bundles. `market` defaults to "indonesia" when a caller
 * has no market — see the enqueuer for why (every pre-migration row is IDR).
 */
export function intentPriceLine({
	amountIdr,
	productKey,
	locale,
	market,
}: {
	amountIdr: number;
	productKey?: string | null;
	locale: "en" | "id";
	market?: "indonesia" | "global" | null;
}): string {
	const resolvedMarket: "indonesia" | "global" =
		market === "global" ? "global" : "indonesia";
	const isAnnual = Boolean(productKey?.startsWith("pro_annual"));
	const interval = isAnnual
		? locale === "id"
			? "tahun"
			: "year"
		: locale === "id"
			? "bulan"
			: "month";
	if (resolvedMarket === "global") {
		const usdMinor = isAnnual
			? GLOBAL_USD_MINOR.pro_annual
			: GLOBAL_USD_MINOR.pro_monthly;
		return `${formatUsd(usdMinor)}/${interval}`;
	}
	const idrAmount = amountIdr > 0 ? amountIdr : PRODUCTS.pro_monthly.amountIdr;
	return `${formatIdr(idrAmount)}/${interval}`;
}

/**
 * Compact monthly Pro price for tight CTAs where the full "Rp 39.000" is too
 * long: "Rp39rb" (id, ribu) / "$9" (en). Same catalog sources as
 * {@link monthlyPriceDisplay} — never a literal. Fixes the drift where an `en`
 * CTA showed the IDR compact ("Rp39k") to users who actually pay USD ($9).
 */
export function monthlyPriceDisplayCompact(locale: string): string {
	return locale === "id"
		? `Rp${Math.round(PRODUCTS.pro_monthly.amountIdr / 1000)}rb`
		: formatUsd(GLOBAL_USD_MINOR.pro_monthly);
}

/** The lead annual price string for a given locale, from the live catalog. */
export function annualPriceDisplay(locale: string): string {
	return locale === "id"
		? formatIdr(PRODUCTS.pro_annual.amountIdr)
		: formatUsd(GLOBAL_USD_MINOR.pro_annual);
}

/**
 * Full IDR display string for ANY catalog product ("Rp 490.000"), sourced from
 * `PRODUCTS[key].amountIdr`. Extends the single-source rule beyond seeker Pro to
 * every family (employer, etc.) so a page/component never hardcodes an amount
 * that silently drifts from the catalog on the next reprice — the employer
 * variant of the 2026-06-21 seeker-price drift. IDR-only: the families that use
 * this (employer standard) charge on the Mayar/IDR rail regardless of locale.
 */
export function productIdrDisplay(key: ProductKey): string {
	return formatIdr(PRODUCTS[key].amountIdr);
}

/**
 * Compact IDR for tight CTAs / plan chips ("Rp 490rb"), same catalog source as
 * {@link productIdrDisplay}. Uses the `rb` (ribu) convention shared with
 * {@link monthlyPriceDisplayCompact}, so employer plan copy formats like the
 * rest of the id UI instead of an ad-hoc "Rp 490K" literal.
 */
export function productIdrCompact(key: ProductKey): string {
	return `Rp${Math.round(PRODUCTS[key].amountIdr / 1000)}rb`;
}

/** The "/month" suffix word for a locale (no leading slash). */
export function perMonthWord(locale: string): string {
	return locale === "id" ? "bulan" : "month";
}

/** Short "/mo" suffix word for a locale. */
export function perMonthWordShort(locale: string): string {
	return locale === "id" ? "bln" : "mo";
}

/**
 * Pre-built CTA labels used by the landing upsells, in one place so copy +
 * price can never drift. `loc` accepts the page locale string; non-`id` is
 * treated as English.
 */
export function landingCtaLabels(locale: string): {
	/** Hero "unlock all" CTA, e.g. "Unlock all — $9/month". */
	unlockAll: string;
	/** Bottom "see plans" CTA, e.g. "See plans — $9/mo". */
	seePlans: string;
} {
	const loc: DisplayLocale = locale === "id" ? "id" : "en";
	const monthly = monthlyPriceDisplay(locale);
	if (loc === "id") {
		return {
			unlockAll: `Lihat harga — ${monthly}/${perMonthWord(locale)}`,
			seePlans: `Lihat paket — ${monthly}/${perMonthWordShort(locale)}`,
		};
	}
	return {
		unlockAll: `Unlock all — ${monthly}/${perMonthWord(locale)}`,
		seePlans: `See plans — ${monthly}/${perMonthWordShort(locale)}`,
	};
}
